import { useState, useEffect, useRef, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { SystemError } from "../../errors/sentryErrors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSuitcaseRolling, 
  faClipboardList, 
  faShirt, 
  faPumpSoap, 
  faPlug, 
  faBriefcaseMedical, 
  faHiking, 
  faBaby,
  faBox
} from "@fortawesome/free-solid-svg-icons";
const CATEGORY_CONFIG = {
  essentials: {
    title: "Essentials",
    icon: faClipboardList,
    color: "bg-red-50 border-red-200",
    iconBg: "bg-red-100",
    iconColor: "text-red-500",
  },
  clothing: {
    title: "Clothing",
    icon: faShirt,
    color: "bg-blue-50 border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-500",
  },
  toiletries: {
    title: "Toiletries",
    icon: faPumpSoap,
    color: "bg-purple-50 border-purple-200",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-500",
  },
  electronics: {
    title: "Electronics",
    icon: faPlug,
    color: "bg-yellow-50 border-yellow-200",
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-600",
  },
  healthAndSafety: {
    title: "Health & Safety",
    icon: faBriefcaseMedical,
    color: "bg-green-50 border-green-200",
    iconBg: "bg-green-100",
    iconColor: "text-green-500",
  },
  activitySpecific: {
    title: "Activity Specific",
    icon: faHiking,
    color: "bg-orange-50 border-orange-200",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-500",
  },
  forKids: {
    title: "For Kids",
    icon: faBaby,
    color: "bg-pink-50 border-pink-200",
    iconBg: "bg-pink-100",
    iconColor: "text-pink-500",
  },
};

const EditableItem = ({ value, isChecked, onToggle, onSave, onDelete, isEditMode }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue.trim() && editValue.trim() !== value) {
      onSave(editValue.trim());
    }
  };

  if (isEditMode && isEditing) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-white">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") {
              setEditValue(value);
              setIsEditing(false);
            }
          }}
          className="flex-1 px-2 py-1 border border-teal-400 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-lg transition-all group cursor-pointer ${
        isChecked ? "bg-white/80" : "hover:bg-white/50"
      }`}
      onClick={!isEditMode ? onToggle : undefined}
    >
      <input
        type="checkbox"
        checked={isChecked || false}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
      />
      <span
        onClick={isEditMode ? (e) => { e.stopPropagation(); setIsEditing(true); } : undefined}
        className={`flex-1 text-sm select-none ${isEditMode ? 'cursor-pointer hover:bg-teal-50 rounded px-1 -mx-1' : ''} ${
          isChecked ? "text-gray-400 line-through" : "text-gray-700"
        }`}
        title={isEditMode ? "Click to edit" : undefined}
      >
        {value}
      </span>
      {isEditMode && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
            className="p-1 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors cursor-pointer"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

const PackingChecklistSection = ({ packingChecklist, tripId, onUpdate, checkedItemsData, onCheckedItemsChange, originalPackingChecklist }) => {
  const [checkedItems, setCheckedItems] = useState(() => {
    if (checkedItemsData && Object.keys(checkedItemsData).length > 0) {
      return checkedItemsData;
    }
    if (tripId) {
      const saved = localStorage.getItem(`packing-${tripId}`);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
        }
      }
    }
    return {};
  });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [localChecklist, setLocalChecklist] = useState(packingChecklist || {});
  const [newItemText, setNewItemText] = useState({});
  const [editingCategoryKey, setEditingCategoryKey] = useState(null);
  const [editingCategoryTitle, setEditingCategoryTitle] = useState("");
  const categoryTitleInputRef = useRef(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (editingCategoryKey && categoryTitleInputRef.current) {
      categoryTitleInputRef.current.focus();
    }
  }, [editingCategoryKey]);

  useEffect(() => {
    if (packingChecklist) {
      setLocalChecklist(packingChecklist);
    }
  }, [packingChecklist]);

  useEffect(() => {
    if (checkedItemsData && Object.keys(checkedItemsData).length > 0) {
      setCheckedItems(checkedItemsData);
      hasInitializedRef.current = true;
    }
  }, [checkedItemsData]);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (checkedItemsData && Object.keys(checkedItemsData).length > 0) return;

    if (tripId) {
      const saved = localStorage.getItem(`packing-${tripId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Object.keys(parsed).length > 0) {
            setCheckedItems(parsed);
            if (onCheckedItemsChange) {
              onCheckedItemsChange(parsed);
            }
            hasInitializedRef.current = true;
          }
        } catch (e) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "system");
            scope.setTag("app", "web");
            scope.setTag("error_source", "browser");
            Sentry.captureException(new SystemError(e instanceof Error ? e.message : String(e)));
          });
          console.error("Error loading packing checklist state:", e);
        }
      }
    }
  }, [tripId, checkedItemsData, onCheckedItemsChange]);

  const saveCheckedItems = useCallback((newCheckedItems) => {
    if (tripId) {
      localStorage.setItem(`packing-${tripId}`, JSON.stringify(newCheckedItems));
    }
    if (onCheckedItemsChange) {
      onCheckedItemsChange(newCheckedItems);
    }
  }, [tripId, onCheckedItemsChange]);

  if (!localChecklist || typeof localChecklist !== "object") {
    return null;
  }

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setCheckedItems((prev) => {
      const newCheckedItems = {
        ...prev,
        [key]: !prev[key],
      };
      saveCheckedItems(newCheckedItems);
      return newCheckedItems;
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleUpdateItem = (category, index, newValue) => {
    const updated = { ...localChecklist };
    updated[category] = [...updated[category]];
    updated[category][index] = newValue;
    setLocalChecklist(updated);
    onUpdate && onUpdate(updated);
  };

  const handleDeleteItem = (category, index) => {
    const updated = { ...localChecklist };
    updated[category] = updated[category].filter((_, i) => i !== index);
    if (updated[category].length === 0) {
      delete updated[category];
    }
    setLocalChecklist(updated);
    onUpdate && onUpdate(updated);
  };

  const handleAddItem = (category) => {
    const text = newItemText[category]?.trim();
    if (!text) return;

    const updated = { ...localChecklist };
    if (!updated[category]) {
      updated[category] = [];
    }
    updated[category] = [...updated[category], text];
    setLocalChecklist(updated);
    setNewItemText((prev) => ({ ...prev, [category]: "" }));
    onUpdate && onUpdate(updated);
  };

  const categoryHasChanges = (category) => {
    if (!originalPackingChecklist) return false;
    const current = localChecklist[category];
    const original = originalPackingChecklist[category];
    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const startEditingCategory = (category, e) => {
    e.stopPropagation();
    const config = CATEGORY_CONFIG[category];
    setEditingCategoryTitle(config?.title || category);
    setEditingCategoryKey(category);
  };

  const saveCategoryTitleEdit = () => {
    setEditingCategoryKey(null);
    setEditingCategoryTitle("");
  };

  const cancelCategoryTitleEdit = () => {
    setEditingCategoryKey(null);
    setEditingCategoryTitle("");
  };

  const revertCategory = (category, e) => {
    e.stopPropagation();
    if (originalPackingChecklist?.[category]) {
      if (window.confirm("Are you sure you want to revert this category to original?")) {
        const updated = { ...localChecklist };
        updated[category] = [...originalPackingChecklist[category]];
        setLocalChecklist(updated);
        onUpdate && onUpdate(updated);
      }
    }
  };

  const handleAddCategory = () => {
    const newCategoryKey = `custom_${Date.now()}`;
    const updated = { ...localChecklist };
    updated[newCategoryKey] = [];
    setLocalChecklist(updated);
    setExpandedCategories(prev => ({ ...prev, [newCategoryKey]: true }));
    onUpdate && onUpdate(updated);
  };

  const handleDeleteCategory = (category, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this entire category?")) {
      const updated = { ...localChecklist };
      delete updated[category];
      setLocalChecklist(updated);
      onUpdate && onUpdate(updated);
    }
  };

  const getCategoryProgress = (category, items) => {
    if (!items || items.length === 0) return { checked: 0, total: 0 };
    const checked = items.filter((_, idx) => checkedItems[`${category}-${idx}`]).length;
    return { checked, total: items.length };
  };

  const getTotalProgress = () => {
    let totalItems = 0;
    let checkedCount = 0;

    Object.entries(localChecklist).forEach(([category, items]) => {
      if (Array.isArray(items) && items.length > 0) {
        totalItems += items.length;
        checkedCount += items.filter((_, idx) => checkedItems[`${category}-${idx}`]).length;
      }
    });

    return { checked: checkedCount, total: totalItems };
  };

  const categories = Object.entries(localChecklist).filter(
    ([_, items]) => Array.isArray(items) && items.length > 0
  );

  if (categories.length === 0) {
    return null;
  }

  const totalProgress = getTotalProgress();
  const progressPercentage = totalProgress.total > 0
    ? Math.round((totalProgress.checked / totalProgress.total) * 100)
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
              <span className="text-xl text-teal-600 flex items-center justify-center">
                <FontAwesomeIcon icon={faSuitcaseRolling} />
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Packing Checklist</h2>
              <p className="text-sm text-gray-500">Personalized for your trip</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-teal-600">{progressPercentage}%</div>
            <p className="text-xs text-gray-500">
              {totalProgress.checked}/{totalProgress.total} packed
            </p>
          </div>
        </div>

        <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.map(([category, items]) => {
            const config = CATEGORY_CONFIG[category] || {
              title: category,
              icon: faBox,
              color: "bg-gray-50 border-gray-200",
              iconBg: "bg-gray-100",
              iconColor: "text-gray-500",
            };
            const progress = getCategoryProgress(category, items);
            const isExpanded = expandedCategories[category];
            const isComplete = progress.checked === progress.total;

            return (
              <div
                key={category}
                className={`rounded-xl border ${config.color} overflow-hidden transition-all duration-200`}
              >
                <div className="w-full p-4 flex items-center justify-between hover:bg-white/50 transition-colors">
                  <div
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className={`w-8 h-8 ${config.iconBg} rounded-lg flex items-center justify-center`}>
                      <span className={`text-base flex items-center justify-center ${config.iconColor || 'text-gray-600'}`}>
                        <FontAwesomeIcon icon={config.icon} />
                      </span>
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{config.title}</h3>
                      <p className="text-xs text-gray-500">
                        {progress.checked}/{progress.total} items
                        {isComplete && <span className="ml-1 text-green-600">✓</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isExpanded && categoryHasChanges(category) && (
                      <button
                        onClick={(e) => revertCategory(category, e)}
                        className="p-2 text-amber-500 cursor-pointer hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Revert category to original"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    )}
                    {isExpanded && (
                      <button
                        onClick={(e) => handleDeleteCategory(category, e)}
                        className="p-2 text-gray-400 cursor-pointer hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete category"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="p-2 cursor-pointer hover:bg-white/50 rounded-lg transition-colors"
                    >
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-1">
                    {items.map((item, idx) => (
                      <EditableItem
                        key={idx}
                        value={item}
                        isChecked={checkedItems[`${category}-${idx}`]}
                        onToggle={() => toggleItem(category, idx)}
                        onSave={(newValue) => handleUpdateItem(category, idx, newValue)}
                        onDelete={() => handleDeleteItem(category, idx)}
                        isEditMode={true}
                      />
                    ))}

                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200/50">
                      <input
                        type="text"
                        value={newItemText[category] || ""}
                        onChange={(e) => setNewItemText((prev) => ({ ...prev, [category]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && handleAddItem(category)}
                        placeholder="Add new item..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <button
                        onClick={() => handleAddItem(category)}
                        className="px-3 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {totalProgress.checked > 0 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => { setCheckedItems({}); saveCheckedItems({}); }}
              className="text-sm text-gray-500 hover:text-gray-700 underline cursor-pointer"
            >
              Reset checklist
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackingChecklistSection;
